import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Check, Coins, Zap, Award, ShoppingBag, Star, CheckCircle2, X, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { notificationAPI } from '../lib/api';

const NOTIF_ICONS = {
  coins: Coins, xp: Zap, achievement: Award, purchase: ShoppingBag,
  level_up: Star, activity: CheckCircle2, activity_approval: CheckCircle2, 
  activity_rejected: XCircle, info: Bell,
};

const NOTIF_COLORS = {
  coins: 'text-amber-500 bg-amber-50', xp: 'text-purple-500 bg-purple-50',
  achievement: 'text-yellow-500 bg-yellow-50', purchase: 'text-blue-500 bg-blue-50',
  level_up: 'text-emerald-500 bg-emerald-50', activity: 'text-green-500 bg-green-50',
  activity_approval: 'text-orange-500 bg-orange-50', activity_rejected: 'text-red-500 bg-red-50',
  info: 'text-slate-500 bg-slate-50',
};

export default function NotificationSheet({ open, onClose, user }) {
  const [notifications, setNotifications] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch {}
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] sm:hidden" data-testid="notification-sheet">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[75vh] flex flex-col animate-in slide-in-from-bottom duration-200"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0.5rem)' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
          <h3 className="font-bold text-base text-slate-900">Notificaciones</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600" onClick={handleMarkAllRead}>
                <Check className="w-3 h-3 mr-1" />Marcar todo
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">Sin notificaciones</p>
            </div>
          ) : (
            notifications.slice(0, 50).map(notif => {
              const Icon = NOTIF_ICONS[notif.type] || Bell;
              const colorClass = NOTIF_COLORS[notif.type] || NOTIF_COLORS.info;
              return (
                <div
                  key={notif.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 active:bg-slate-100 ${
                    notif.read ? '' : 'bg-blue-50/40'
                  }`}
                  onClick={() => !notif.read && handleMarkRead(notif.id)}
                  data-testid={`notif-sheet-item-${notif.id}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium truncate ${notif.read ? 'text-slate-600' : 'text-slate-900'}`}>
                        {notif.title}
                      </p>
                      {!notif.read && <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">{notif.message}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(notif.created_at)}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
